/**
 * QC Trade — Featured products: seamless infinite carousel
 *
 * Horizon only does a true reorder-loop when a single slide is visible.
 * With several cards visible, wrap jumps/rewinds — that feels broken.
 *
 * This script takes over the OUTER featured product-list carousel only:
 *  1. Clones head/tail slides (classic infinite strip)
 *  2. Drives the track with CSS transform (smooth)
 *  3. On landing in a clone zone, instantly repositions to the matching real slide
 *     so last -> first (and first -> last) never flash or rewind
 *
 * Live markup notes (Horizon):
 *  - Every product card contains its OWN <slideshow-component> (card-gallery image carousel,
 *    with its own <slideshow-arrows> when the product has 3+ images). Those must never be
 *    touched: only the carousel rendered by resource-list-carousel
 *    (slideshow-component.resource-list__carousel, not nested in another slideshow) is used.
 *  - The prev/next <slideshow-arrows> sit inside <slideshow-container>, which clips. They are
 *    moved one level up, to be a direct child of the same <slideshow-component>, so they can sit
 *    in the side gutters (outside the clip) while staying inside the component.
 *
 * Sizing contract with qc-corporate-sections.css:
 *  - Cards per view come from the CSS custom property --qc-per-view (5 / 3 / 2).
 *  - The visible area is the CONTENT box of <slideshow-container> (its padding is a
 *    "bleed" so card shadows / hover lift are not clipped). JS publishes that width
 *    as --qc-track-viewport; CSS sizes each card from it.
 *  - The translate step is measured from the DOM (distance between two adjacent
 *    real slides = card width + computed gap), never from constants, and is
 *    re-measured on every resize. Position is always index * step, so it cannot drift.
 */
(function () {
  const SECTION = '.shopify-section';
  const PRODUCT_LIST = '[data-testid="product-list"]';
  const CAROUSEL = 'slideshow-component.resource-list__carousel';
  const DURATION = 420;
  const TRANSITION = 'transform ' + DURATION + 'ms cubic-bezier(0.22, 1, 0.36, 1)';
  const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';

  function featuredSections() {
    return Array.from(document.querySelectorAll(SECTION)).filter((s) =>
      s.querySelector(PRODUCT_LIST)
    );
  }

  /** Outer featured carousels only (never the per-card image slideshows or clones). */
  function featuredCarousels(section) {
    return Array.from(section.querySelectorAll(CAROUSEL)).filter(
      (el) =>
        !el.parentElement.closest('slideshow-component') &&
        !el.closest('[data-qc-clone]') &&
        !el.closest('.card-gallery, product-card')
    );
  }

  function perView(slideshow) {
    const raw = parseInt(getComputedStyle(slideshow).getPropertyValue('--qc-per-view'), 10);
    if (raw > 0) return raw;
    const w = window.innerWidth || document.documentElement.clientWidth;
    if (w < 750) return 2;
    if (w < 990) return 3;
    return 5;
  }

  function cloneSlide(slide) {
    const c = slide.cloneNode(true);
    c.setAttribute('data-qc-clone', '1');
    c.setAttribute('aria-hidden', 'true');
    c.removeAttribute('reveal');
    c.removeAttribute('id');
    c.removeAttribute('ref'); // keep Horizon's refs.slides limited to the real slides
    // Avoid duplicate IDs and keep clones out of the tab order (they are visual copies)
    c.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    c.querySelectorAll(FOCUSABLE).forEach((node) => node.setAttribute('tabindex', '-1'));
    return c;
  }

  /**
   * @param {HTMLElement} slideshow
   */
  function takeover(slideshow) {
    if (!slideshow || slideshow.dataset.qcSeamless === '1') return;
    const container = slideshow.querySelector(':scope > slideshow-container');
    const track = container && container.querySelector(':scope > slideshow-slides');
    if (!track) return;

    // Strip clones left by a previous init (theme editor reloads)
    track.querySelectorAll(':scope > [data-qc-clone]').forEach((n) => n.remove());

    const realSlides = Array.from(track.children).filter(
      (el) =>
        el.tagName &&
        el.tagName.toLowerCase() === 'slideshow-slide' &&
        !el.hasAttribute('data-qc-clone') &&
        !el.hasAttribute('hidden')
    );
    if (realSlides.length < 2) return;

    slideshow.dataset.qcSeamless = '1';
    slideshow.classList.add('qc-featured-seamless');

    // Move this carousel's own arrows out of the clipping container, but keep them
    // inside the same <slideshow-component> (CSS puts them in the side gutters).
    const arrows =
      container.querySelector(':scope > slideshow-arrows') ||
      slideshow.querySelector(':scope > slideshow-arrows');
    if (arrows) {
      arrows.classList.add('qc-fc-arrows');
      if (arrows.parentElement !== slideshow) slideshow.appendChild(arrows);
    }

    const n = realSlides.length;
    let v = 0; // clones on each side (= cards per view, capped at n)
    let index = 0; // position in the full strip, including leading clones
    let step = 0;
    let animating = false;
    let settleTimer = null;

    // Kill Horizon scroll-snap / overflow so transform owns motion
    slideshow.setAttribute('infinite', '');
    track.style.display = 'flex';
    track.style.willChange = 'transform';
    track.style.scrollSnapType = 'none';
    track.style.overflow = 'visible';
    track.scrollLeft = 0;

    function buildClones() {
      const realIndex = v ? (((index - v) % n) + n) % n : 0;
      track.querySelectorAll(':scope > [data-qc-clone]').forEach((c) => c.remove());
      v = Math.min(perView(slideshow), n);
      const head = realSlides.slice(-v).map(cloneSlide);
      const tail = realSlides.slice(0, v).map(cloneSlide);
      head.reverse().forEach((c) => track.insertBefore(c, track.firstChild));
      tail.forEach((c) => track.appendChild(c));
      index = v + realIndex;
    }

    function viewportWidth() {
      const cs = getComputedStyle(container);
      const w =
        container.clientWidth -
        (parseFloat(cs.paddingLeft) || 0) -
        (parseFloat(cs.paddingRight) || 0);
      return Math.max(0, w);
    }

    function measureStep() {
      const a = realSlides[0].getBoundingClientRect();
      const b = realSlides[1].getBoundingClientRect();
      const s = b.left - a.left;
      if (s > 0) return s;
      // Fallback: card width + computed gap
      const cs = getComputedStyle(track);
      return a.width + (parseFloat(cs.columnGap || cs.gap) || 0);
    }

    function apply(animate) {
      track.style.transition = animate ? TRANSITION : 'none';
      track.style.transform = 'translate3d(' + -(index * step) + 'px, 0, 0)';
    }

    function normalize() {
      clearTimeout(settleTimer);
      settleTimer = null;
      if (index >= v + n) index -= n;
      else if (index < v) index += n;
      apply(false);
      void track.offsetWidth; // flush so the next move animates
      animating = false;
    }

    function go(delta) {
      if (animating || !step) return;
      animating = true;
      index += delta;
      apply(true);
      // Safety net if transitionend never fires (hidden tab, reduced motion, etc.)
      settleTimer = setTimeout(normalize, DURATION + 120);
    }

    track.addEventListener('transitionend', (e) => {
      if (e.target !== track || e.propertyName !== 'transform') return;
      normalize();
    });

    let lastWidth = -1;
    let lastPerView = -1;
    function refreshMetrics(force) {
      const pv = perView(slideshow);
      if (pv !== lastPerView) {
        lastPerView = pv;
        buildClones();
      }
      const width = viewportWidth();
      if (width > 0) slideshow.style.setProperty('--qc-track-viewport', width + 'px');
      if (!force && width === lastWidth && step) return;
      lastWidth = width;
      step = measureStep();
      if (animating) normalize();
      apply(false);
    }

    // 1) Build clones + measure first ...
    refreshMetrics(true);
    requestAnimationFrame(() => refreshMetrics(true));

    // ... 2) then route Horizon's next/previous (keyboard etc.) to the strip ...
    slideshow.next = function qcSeamlessNext(event) {
      event && event.preventDefault && event.preventDefault();
      go(1);
    };
    slideshow.previous = function qcSeamlessPrevious(event) {
      event && event.preventDefault && event.preventDefault();
      go(-1);
    };

    // ... 3) and bind this carousel's two arrow buttons exactly once.
    // Horizon's declarative on:click="/previous|/next" is removed from these two buttons so a
    // click has exactly one handler (ours) instead of Horizon's delegate + ours.
    if (arrows) {
      arrows.querySelectorAll('.slideshow-control').forEach((btn) => {
        if (btn.dataset.qcBound === '1') return;
        btn.dataset.qcBound = '1';
        btn.removeAttribute('on:click');
        btn.disabled = false;
        btn.removeAttribute('disabled');
        btn.setAttribute('type', 'button');
        const dir = btn.classList.contains('slideshow-control--previous') ? -1 : 1;
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          go(dir);
        });
      });
    }

    // Touch swipe (arrows are hidden on small screens)
    let sx = 0;
    let sy = 0;
    let tracking = false;
    track.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length !== 1) return;
        tracking = true;
        sx = e.touches[0].clientX;
        sy = e.touches[0].clientY;
      },
      { passive: true }
    );
    track.addEventListener(
      'touchend',
      (e) => {
        if (!tracking) return;
        tracking = false;
        const t = e.changedTouches[0];
        const dx = t.clientX - sx;
        const dy = t.clientY - sy;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) go(dx < 0 ? 1 : -1);
      },
      { passive: true }
    );

    // Disable native horizontal wheel scrolling fighting the transform track
    track.addEventListener(
      'wheel',
      (e) => {
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) e.preventDefault();
      },
      { passive: false }
    );
    // Horizon may try to scroll the strip natively; keep it pinned at 0
    track.addEventListener('scroll', () => {
      if (track.scrollLeft !== 0) track.scrollLeft = 0;
    });

    let resizeTimer;
    const scheduleRefresh = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => refreshMetrics(false), 80);
    };
    window.addEventListener('resize', scheduleRefresh);
    if ('ResizeObserver' in window) new ResizeObserver(scheduleRefresh).observe(container);

    // Theme editor section reload safety
    slideshow._qcSeamlessRefresh = () => refreshMetrics(true);
  }

  function boot() {
    featuredSections().forEach((section) => {
      featuredCarousels(section).forEach(takeover);
    });
  }

  function start() {
    const run = () => {
      boot();
      let k = 0;
      const t = setInterval(() => {
        boot();
        if (++k >= 12) clearInterval(t);
      }, 200);
    };
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('slideshow-component').then(run).catch(run);
    } else {
      run();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', () => {
    featuredSections().forEach((section) => {
      featuredCarousels(section).forEach((el) => {
        delete el.dataset.qcSeamless;
      });
    });
    boot();
  });
})();
